import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import {
  isClinicalWriteConflict,
  type PatientDemographicsUpdateInput,
  type PatientProfile,
  type PatientProfileUpdateInput,
} from "../../api/clinicalWorkspace";
import type { ProfileSectionKey } from "./profileSections";
import {
  newProfileIdempotencyKey,
  type PatientProfileClient,
  patientProfileKey,
  useUpdatePatientDemographics,
  useUpdatePatientProfile,
} from "./usePatientProfile";

/**
 * One section edits at a time. This hook owns the edit session (which
 * section, a reset key the editors key their drafts on), the idempotency key
 * (rotated after every settled write, success or conflict, so a retry of a
 * failed network call replays safely while a new attempt never collides),
 * the server error shown inside the editor, and the "Saved" status.
 *
 * The editor and its version token belong to one snapshot. Refetches must
 * never combine an old replace-set with a new token. On a 409, keep the
 * drafts visible but require an explicit reload before saving again.
 */
export const STALE_SECTION_MESSAGE =
  "This record changed elsewhere. Your unsaved edits are still here. Reload the latest section before editing and saving again.";

export interface SectionEditing {
  readonly editing: ProfileSectionKey | null;
  readonly resetKey: string;
  readonly saving: boolean;
  readonly error: string | null;
  readonly savedSection: ProfileSectionKey | null;
  readonly dirty: boolean;
  readonly editProfile: PatientProfile | undefined;
  readonly conflicted: boolean;
  begin(section: ProfileSectionKey): void;
  cancel(): void;
  setDirty(dirty: boolean): void;
  saveSection(profile: PatientProfileUpdateInput): Promise<void>;
  saveDemographics(demographics: PatientDemographicsUpdateInput): Promise<void>;
  reload(): Promise<void>;
  /** True when navigation may proceed; asks first when an editor has unsaved changes. */
  confirmLeave(): boolean;
}

export function useSectionEditing(
  client: PatientProfileClient,
  siteId: string,
  patientId: string,
  profile: PatientProfile | undefined,
): SectionEditing {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ProfileSectionKey | null>(null);
  const [session, setSession] = useState(0);
  const [idempotencyKey, setIdempotencyKey] = useState(newProfileIdempotencyKey);
  const [error, setError] = useState<string | null>(null);
  const [savedSection, setSavedSection] = useState<ProfileSectionKey | null>(null);
  const [dirty, setDirty] = useState(false);
  const [editProfile, setEditProfile] = useState<PatientProfile>();
  const [conflicted, setConflicted] = useState(false);
  const [reloading, setReloading] = useState(false);
  const sessionGeneration = useRef(0);
  const updateProfile = useUpdatePatientProfile(client, siteId, patientId);
  const updateDemographics = useUpdatePatientDemographics(client, siteId, patientId);

  const begin = useCallback(
    (section: ProfileSectionKey) => {
      if (profile === undefined) return;
      sessionGeneration.current += 1;
      setReloading(false);
      setEditProfile(profile);
      setConflicted(false);
      setIdempotencyKey(newProfileIdempotencyKey());
      setEditing(section);
      setSession((current) => current + 1);
      setError(null);
      setSavedSection(null);
      setDirty(false);
    },
    [profile],
  );

  const cancel = useCallback(() => {
    sessionGeneration.current += 1;
    setReloading(false);
    setEditing(null);
    setError(null);
    setDirty(false);
  }, []);

  const settle = useCallback(
    async (caught: unknown) => {
      if (isClinicalWriteConflict(caught)) {
        setConflicted(true);
        setError(STALE_SECTION_MESSAGE);
        setIdempotencyKey(newProfileIdempotencyKey());
        await queryClient.invalidateQueries({ queryKey: patientProfileKey(siteId, patientId) });
        return;
      }
      setError(caught instanceof Error ? caught.message : "The section could not be saved.");
    },
    [queryClient, siteId, patientId],
  );

  const reload = useCallback(async () => {
    if (!conflicted || reloading) return;
    if (dirty && !window.confirm("Replace your unsaved edits with the latest saved section?"))
      return;
    const generation = sessionGeneration.current;
    setReloading(true);
    try {
      const latest = await queryClient.fetchQuery({
        queryKey: patientProfileKey(siteId, patientId),
        queryFn: ({ signal }) => client.getPatientProfile(siteId, patientId, signal),
        staleTime: 0,
      });
      // Leaving this editor must not let its pending reload reset a new draft.
      if (sessionGeneration.current !== generation) return;
      setEditProfile(latest);
      setSession((current) => current + 1);
      setIdempotencyKey(newProfileIdempotencyKey());
      setConflicted(false);
      setDirty(false);
      setError(null);
    } catch {
      if (sessionGeneration.current !== generation) return;
      setError(
        "The latest section could not be loaded. Your unsaved edits are still here. Try reloading again.",
      );
    } finally {
      if (sessionGeneration.current === generation) setReloading(false);
    }
  }, [client, conflicted, dirty, patientId, queryClient, reloading, siteId]);

  const finish = useCallback((section: ProfileSectionKey | null) => {
    setIdempotencyKey(newProfileIdempotencyKey());
    setSavedSection(section);
    setEditing(null);
    setError(null);
    setDirty(false);
  }, []);

  const saveSection = useCallback(
    async (sectionProfile: PatientProfileUpdateInput) => {
      if (editProfile === undefined || conflicted || reloading) return;
      setError(null);
      try {
        await updateProfile.mutateAsync({
          idempotencyKey,
          expectedUpdatedAt: editProfile.updatedAt,
          profile: sectionProfile,
        });
        finish(editing);
      } catch (caught) {
        await settle(caught);
      }
    },
    [editProfile, conflicted, reloading, updateProfile, idempotencyKey, editing, finish, settle],
  );

  const saveDemographics = useCallback(
    async (demographics: PatientDemographicsUpdateInput) => {
      if (conflicted || reloading) return;
      setError(null);
      try {
        await updateDemographics.mutateAsync({ idempotencyKey, demographics });
        finish("demographics");
      } catch (caught) {
        await settle(caught);
      }
    },
    [conflicted, reloading, updateDemographics, idempotencyKey, finish, settle],
  );

  const confirmLeave = useCallback(() => {
    if (editing === null || !dirty) return true;
    return window.confirm("Discard the unsaved changes in this section?");
  }, [editing, dirty]);

  return {
    editing,
    resetKey: `${patientId}:${editing ?? "none"}:${session}`,
    saving: updateProfile.isPending || updateDemographics.isPending || reloading,
    error,
    savedSection,
    dirty,
    editProfile,
    conflicted,
    reload,
    begin,
    cancel,
    setDirty,
    saveSection,
    saveDemographics,
    confirmLeave,
  };
}
